/**
 * The trips page headline. A dated trip is the most interesting thing the page knows about a
 * traveler, so the headline leads with it — a countdown before departure ("2 days until Bahamas"),
 * a send-off while they're away ("Enjoy Tokyo!") — and falls back to a plain greeting when there's
 * nothing dated to talk about.
 *
 * Pure and date-injected (no `new Date()` inside) so the copy is testable at any point relative
 * to a trip.
 */
import { differenceInCalendarDays } from 'date-fns';

/** The minimum a trip has to offer the headline: something to call it, and when it runs. */
export interface GreetingTrip {
  /** Destination name where we have one, else the trip's own title. */
  label: string;
  startDate?: Date;
  endDate?: Date;
}

export function tripsGreeting({
  trips,
  now,
  firstName,
}: {
  trips: GreetingTrip[];
  now: Date;
  firstName?: string;
}): string {
  const greeting = firstName ? `Where to next ${firstName}?` : 'Where to next?';
  const dated = trips.filter((trip): trip is GreetingTrip & { startDate: Date } => Boolean(trip.startDate));

  // Underway: started on or before today and hasn't ended. A trip with no end date only counts on
  // its start day — the same rule the trips list uses to stop treating it as current.
  const underway = dated.find((trip) => {
    const startsIn = differenceInCalendarDays(trip.startDate, now);
    if (startsIn > 0) return false;
    const endsIn = trip.endDate ? differenceInCalendarDays(trip.endDate, now) : startsIn;
    return endsIn >= 0;
  });
  if (underway) return `Enjoy ${underway.label}!`;

  const next = dated
    .filter((trip) => differenceInCalendarDays(trip.startDate, now) > 0)
    .toSorted((a, b) => a.startDate.getTime() - b.startDate.getTime())[0];
  if (next) {
    const days = differenceInCalendarDays(next.startDate, now);
    return days === 1 ? `Tomorrow: ${next.label}` : `${days} days until ${next.label}`;
  }

  return greeting;
}
